import { useContext, useEffect, useMemo, useState } from 'react'

import { MediaQueries } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { DataTrackingTypeHTMLElement } from '@hhgtech/hhg-components/types'
import { Plus } from '@hhgtech/icons/core'
import { Accordion, Box, BoxProps, Stack, Tooltip, rem } from '@mantine/core'
import { useIntl } from 'react-intl'

import { Container } from 'components/atoms/container'
import { Disclaimer } from 'components/atoms/disclaimer'
import { CarouselOrListingBlockWrapper } from 'components/organisms/serviceConnections/carouselOrListingBlockWrapper'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import {
  KeyPillarsClickableBannerType,
  ListBanner,
  SpotlightClickableBanner,
} from 'interfaces/types'
import { actionTypes } from 'state/actionTypes'
import { AppContext } from 'state/appContext'

import { Banner } from './banner'
import { BannerList } from './BannerList'
import useStyles from './index.styles'

type ClickableBannerProps = {
  banner: SpotlightClickableBanner
  bannerProps?: BoxProps & DataTrackingTypeHTMLElement
  listingProps?: BoxProps
  bgColor?: string
  hasDisclaimer?: boolean
  embedChatwootClick?: boolean
  chatToken?: string
  avatarIcon?: string
  applyCarousel?: boolean
  requireLogin?: boolean
  onItemClick?: (v: string) => void
  applyH1?: boolean
  openAllAccordion?: boolean
} & BoxProps

export type BannerProps = {
  banner: KeyPillarsClickableBannerType[]
  hasLeavePageConfirm?: boolean
  bgSessionColor?: string
  applyCarousel?: boolean
  requireLogin?: boolean
} & BoxProps &
  DataTrackingTypeHTMLElement

export const ClickableBanner = ({
  embedChatwootClick,
  chatToken,
  avatarIcon,
  banner,
  bannerProps,
  bgColor,
  hasDisclaimer,
  applyCarousel,
  requireLogin,
  onItemClick,
  styles,
  classNames,
  applyH1,
  openAllAccordion,
}: ClickableBannerProps) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const currentUrl = useCurrentUrl()
  const { dispatch } = useContext(AppContext)
  const listBanners = banner.list_banners
  const isPillarType = Boolean(banner.listing?.[0]?.keyPillars)
  const { classes } = useStyles(
    {
      listLength: listBanners.length,
      bgColor,
      embedChatwootClick,
      isPillarType,
    },
    { name: 'ClickableBanner', styles, classNames },
  )
  const isExternalLinkChat = useMemo(() => chatToken?.startsWith('https'), [])

  // Accordion state
  const [openedItem, setOpenedItem] = useState<string | null>(
    openAllAccordion ? null : '0',
  )

  useEffect(() => {
    !isExternalLinkChat &&
      chatToken &&
      dispatch({
        type: actionTypes.SET_CHATWOOT_WEBSITE_TOKEN,
        payload: chatToken,
      })
  }, [chatToken])

  useEffect(() => {
    const bodyEle = document.getElementsByTagName('body')[0]
    if (avatarIcon) {
      bodyEle.classList.add('replace-chatwoot-icon')
      document.documentElement.style.setProperty(
        '--chatwoot-bg',
        `url('${avatarIcon}')`,
      )
    }
    return () => {
      bodyEle.classList.remove('replace-chatwoot-icon')
    }
  }, [avatarIcon])

  if (!banner) return null
  const commonBannerProps = {
    classNames: {
      banner: classes.bannerItemWrapper,
      bannerIframe: classes.bannerIframe,
      title: classes.bannerItemTitle,
    },
    requireLogin,
    'data-event-category': 'Spotlight',
    'data-event-action': 'Banners Click',
    bgSessionColor: bgColor,
    keepNatureRatio: embedChatwootClick,
    ratio: listBanners.length === 1 ? 16 / 9 : undefined,
  }

  const renderList = ({
    listBanners,
    isExternalLinkChat,
    isChatWoot,
    isPillarType,
  }: {
    listBanners: ListBanner[]
    isExternalLinkChat?: boolean
    isChatWoot?: boolean
    isPillarType?: boolean
  }) => (
    <CarouselOrListingBlockWrapper
      showFullSlide
      lengthToCarouselDesktop={5}
      gapDesktop={18}
      disableCarousel={!applyCarousel}
    >
      <CarouselOrListingBlockWrapper.Slider
        arrow
        classNames={{
          listing: classes.bannerList,
          itemWrapper: classes.bannerItem,
        }}
        styles={(theme) => ({
          carouselViewport: {
            paddingTop: 4,
            paddingBottom: 10,
            paddingLeft: 4,
            [theme.fn.smallerThan('sm')]: {
              paddingLeft: 16,
              paddingRight: 16,
            },
          },
          progressRoot: {
            background: 'transparent',
            position: 'relative',
            [theme.fn.smallerThan('sm')]: {
              width: `calc(100% - 48px)`,
              marginLeft: 16,
            },
            '&:before': {
              position: 'absolute',
              content: '""',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: '#fff',
              opacity: 0.25,
            },
          },
          progressBar: {
            background: theme.colors.neutral[3],
          },
        })}
      >
        {listBanners.map((_banner) => (
          <Banner
            className={isPillarType ? 'with-shadow' : ''}
            onItemClick={onItemClick}
            key={_banner.title}
            banner={_banner}
            openChatWoot={
              !isExternalLinkChat &&
              (embedChatwootClick || _banner.url.endsWith('#chatwoot'))
            }
            {...commonBannerProps}
            {...bannerProps}
            {...(isChatWoot && {
              'data-event-category': 'Chatwoot Chat',
              'data-event-action': 'Start Chat - Middle Page',
              'data-event-label': currentUrl,
            })}
            styles={(theme) => ({
              banner: {
                ...(isPillarType && {
                  boxShadow: theme.shadows.sm,
                }),
              },
            })}
          ></Banner>
        ))}
      </CarouselOrListingBlockWrapper.Slider>
    </CarouselOrListingBlockWrapper>
  )

  const content = ({
    isExternalLinkChat = false,
    isChatWoot = false,
  }: {
    isExternalLinkChat?: boolean
    isChatWoot?: boolean
  }) => (
    <Box className={classes.root}>
      <Container className={classes.inner}>
        <Stack
          w="100%"
          sx={(theme) => ({
            gap: rem(16),
            [theme.fn.largerThan('sm')]: { gap: rem(24) },
          })}
        >
          {(banner.icon || banner.title || banner.description) && (
            <Stack spacing={12} className={classes.header}>
              {banner.icon && (
                <Box
                  sx={{
                    backgroundColor: '#ffffff',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    padding: 6,
                    [MediaQueries.mbDown]: {
                      width: 64,
                      height: 64,
                    },
                  }}
                >
                  <img src={banner.icon} alt="" loading="lazy" />
                </Box>
              )}
              {banner.title && (
                <Text
                  size="h3"
                  as={applyH1 ? 'h1' : 'h3'}
                  className={classes.heading}
                >
                  {banner.title}
                </Text>
              )}
              {banner.description && (
                <Text size="p3" className={classes.desc}>
                  {banner.description}
                </Text>
              )}
            </Stack>
          )}
          {banner.listing?.[0]?.keyPillars ? (
            <Box
              sx={(theme) => ({
                [theme.fn.smallerThan('sm')]: {
                  margin: '0 -16px',
                },
              })}
            >
              <Accordion
                defaultValue={
                  openAllAccordion
                    ? banner.listing.map((_i, idx) => idx.toString())
                    : '0'
                }
                value={openedItem}
                onChange={(val) => setOpenedItem(val as string | null)}
                multiple={openAllAccordion}
                chevron={<Plus size={isMobile ? 18 : 24} />}
                styles={(theme) => ({
                  label: {
                    padding: '12px 16px',
                  },
                  control: {
                    paddingLeft: 0,
                    paddingRight: 10,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '&[data-active="true"]': {
                      '.label-text': {
                        fontWeight: 600,
                      },
                    },
                  },
                  content: {
                    paddingLeft: 1,
                    paddingRight: 0,
                    [theme.fn.smallerThan('sm')]: {
                      padding: 0,
                      paddingBottom: 10,
                    },
                  },
                  item: {
                    borderBottom: `1px solid ${theme.colors.neutral[1]}`,
                    '&:last-of-type': {
                      borderBottom: 0,
                    },
                  },
                  chevron: {
                    '&[data-rotate]': {
                      transform: 'rotate(45deg)',
                    },
                  },
                })}
              >
                {banner.listing.map((i, idx) => (
                  <Accordion.Item key={idx} value={String(idx)}>
                    <Accordion.Control
                      data-event-category="Service Connection"
                      data-event-action={`Expand Banner - ${i.keyPillars?.name}`}
                      data-event-label={currentUrl}
                      className={'pointer-event-child-none'}
                    >
                      <Text size="p3" className="label-text">
                        {i.keyPillars?.name}
                      </Text>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Box
                        sx={(theme) => ({
                          minHeight: 340,
                          [theme.fn.smallerThan('sm')]: {
                            minHeight: 435,
                          },
                        })}
                      >
                        {openedItem === String(idx) && (
                          <BannerList
                            key={`banner-list-${idx}`}
                            listBanners={i.banners}
                            isExternalLinkChat={isExternalLinkChat}
                            isChatWoot={isChatWoot}
                            isPillarType={isPillarType}
                            commonBannerProps={commonBannerProps}
                            bannerProps={bannerProps}
                            classes={classes}
                            currentUrl={currentUrl}
                            embedChatwootClick={embedChatwootClick}
                            onItemClick={onItemClick}
                            applyCarousel={applyCarousel}
                          />
                        )}
                      </Box>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Box>
          ) : (
            renderList({
              isExternalLinkChat,
              isChatWoot,
              listBanners: banner.listing?.[0]?.banners,
            })
          )}
          {hasDisclaimer && (
            <Disclaimer
              classNames={{ root: classes.disclaimer }}
              title={f({
                id: 'confirmLeavePage.title',
                defaultMessage: 'Leaving HHG page',
              })}
              content={f({
                id: 'confirmLeavePage.content',
                defaultMessage:
                  'As your trusted health partner, we want to ensure you have the information you need to safeguard your health. Are you sure you want to leave this page?',
              })}
            />
          )}
        </Stack>
      </Container>
    </Box>
  )

  return !isMobile && embedChatwootClick && !isExternalLinkChat ? (
    <Tooltip.Floating
      label={
        <Box className={classes.bubbleRoot}>
          <Stack
            className={classes.bubbleInside}
            align="center"
            justify="center"
          >
            <Text size="s2" className={classes.bubbleText}>
              {f({
                id: 'carePath.cursorChat.message',
                defaultMessage: 'Chat miễn phí ngay',
              })}
            </Text>
          </Stack>
        </Box>
      }
      styles={{
        tooltip: {
          transition: 'all 0.8s cubic-bezier(0.2, 0.9, 0.3, 1)',
          background: 'transparent',
        },
      }}
    >
      <Box
        data-event-category="Chatwoot Chat"
        data-event-action="Start Chat - Middle Page"
        data-event-label={currentUrl}
        className="pointer-event-child-none"
        onClick={() =>
          isExternalLinkChat
            ? window.open(chatToken)
            : window?.$chatwoot?.toggle('open')
        }
        sx={{
          cursor: 'pointer',
          overflow: 'hidden',
        }}
      >
        {content({ isChatWoot: true })}
      </Box>
    </Tooltip.Floating>
  ) : (
    content({ isExternalLinkChat: true })
  )
}
